export const PROFILE_IMAGE_OUTPUT_SIZE = 512
export const PROFILE_IMAGE_MAX_UPLOAD_BYTES = 900 * 1024
export const PROFILE_IMAGE_MIN_OUTPUT_SIZE = 256
export const MAX_PROFILE_IMAGE_BYTES = 5 * 1024 * 1024
export const PROFILE_IMAGE_ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp,image/gif'

const PROFILE_IMAGE_ALLOWED_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
])

export type CropArea = {
  x: number
  y: number
  width: number
  height: number
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Nao foi possivel carregar a imagem selecionada.'))
    image.src = src
  })
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Nao foi possivel gerar a imagem final.'))
        return
      }

      resolve(blob)
    }, 'image/png')
  })
}

function buildOutputFileName(fileName: string): string {
  const normalized = fileName.trim() || 'foto-entidade'
  return normalized.replace(/\.[^.]+$/, '') + '.png'
}

async function renderSquarePngBlob(
  image: HTMLImageElement,
  cropArea: CropArea,
  size: number
): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Nao foi possivel preparar a imagem final.')
  }

  context.clearRect(0, 0, size, size)
  context.drawImage(
    image,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    size,
    size
  )

  return toBlob(canvas)
}

export function revokeObjectUrl(url: string | null | undefined): void {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

export function validateProfileImageFile(file: File): string | null {
  if (!PROFILE_IMAGE_ALLOWED_TYPES.has(file.type)) {
    return 'Selecione uma imagem PNG, JPG, WEBP ou GIF.'
  }

  if (file.size > MAX_PROFILE_IMAGE_BYTES) {
    return 'A foto deve ter no maximo 5MB.'
  }

  return null
}

export async function cropImageToSquarePng(
  imageSrc: string,
  cropArea: CropArea,
  options?: {
    fileName?: string
    size?: number
    maxBytes?: number
    enforceMaxBytes?: boolean
  }
): Promise<File> {
  const image = await loadImage(imageSrc)
  const maxBytes = options?.maxBytes ?? PROFILE_IMAGE_MAX_UPLOAD_BYTES
  const enforceMaxBytes = options?.enforceMaxBytes ?? true
  let size = options?.size ?? PROFILE_IMAGE_OUTPUT_SIZE
  let blob = await renderSquarePngBlob(image, cropArea, size)

  if (enforceMaxBytes) {
    while (blob.size > maxBytes && size > PROFILE_IMAGE_MIN_OUTPUT_SIZE) {
      const nextSize = Math.max(
        PROFILE_IMAGE_MIN_OUTPUT_SIZE,
        Math.floor(size * 0.75)
      )

      if (nextSize === size) {
        break
      }

      size = nextSize
      blob = await renderSquarePngBlob(image, cropArea, size)
    }

    if (blob.size > maxBytes) {
      throw new Error(
        'A foto final ficou acima do limite de upload. Tente uma imagem com menos detalhes.'
      )
    }
  }

  return new File([blob], buildOutputFileName(options?.fileName ?? 'foto-entidade'), {
    type: 'image/png',
  })
}
